// Typed fetch wrapper that targets the Casemeister backend and attaches
// the Casemeister session token (set after SSO-exchange).

const API_BASE = 'https://app.casemeister.com';
const SESSION_KEY = 'casemeister.session';

export function getStoredSession(): string | null {
  return sessionStorage.getItem(SESSION_KEY);
}

export function setStoredSession(token: string | null): void {
  if (token === null) sessionStorage.removeItem(SESSION_KEY);
  else sessionStorage.setItem(SESSION_KEY, token);
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Skip attaching the session token (used for the SSO-exchange itself). */
  unauthenticated?: boolean;
}

export async function apiRequest<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, unauthenticated = false } = opts;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  if (!unauthenticated) {
    const session = getStoredSession();
    if (session) headers['Authorization'] = `Bearer ${session}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
    // credentials NOT 'include' — we use Bearer tokens, not cookies, so the
    // backend doesn't need to deal with cross-origin cookie quirks from
    // the Outlook add-in iframe.
  });

  if (!res.ok) {
    let errorBody: unknown;
    try { errorBody = await res.json(); } catch { /* non-JSON */ }
    const message = (errorBody as { error?: string })?.error
      || `Verzoek mislukt (HTTP ${res.status})`;
    throw new ApiError(res.status, message, errorBody);
  }

  // 204 No Content: return undefined cast as T (caller knows the shape)
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}
